import React, { useCallback } from 'react';
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { green, grey } from '@mui/material/colors';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { usePdf } from 'hooks/usePdf';
import { LenderRetailActivityDealerRowViewModel } from '../../../api/types';
import {
  DEALER_ACTIVITY_COLUMNS,
  exportDealerActivityToCsv,
  getCountryLabel,
  getDealerActivityTotalRow,
  groupDealerActivityRowsByCountry,
} from '../../../lib';
import { getCountryFlag } from './country';

interface Props {
  rows: LenderRetailActivityDealerRowViewModel[];
  loading?: boolean;
}

const cellSx = { border: '1px solid', borderColor: 'divider', py: 1, px: 1.5 } as const;
const headerCellSx = { ...cellSx, bgcolor: grey[300], fontWeight: 600 } as const;
const totalCellSx = { ...cellSx, bgcolor: green[50], color: green[800], fontWeight: 600 } as const;

export const DealerActivityTable = ({ rows, loading }: Props) => {
  const groups = groupDealerActivityRowsByCountry(rows);
  const totalRow = getDealerActivityTotalRow(rows);
  const colSpan = DEALER_ACTIVITY_COLUMNS.length;
  const { contentRef, handleDownloadPdf } = usePdf();

  const handleExportCsv = useCallback(() => {
    exportDealerActivityToCsv(rows);
  }, [rows]);

  return (
    <Stack gap={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" component="span">
          Dealer Activity
        </Typography>

        <Stack direction="row" gap={1} data-pdf-exclude>
          <Button size="small" variant="outlined" startIcon={<CloudDownloadIcon />} onClick={() => handleDownloadPdf()}>
            Export PDF
          </Button>
          <Button size="small" variant="outlined" startIcon={<CloudDownloadIcon />} onClick={handleExportCsv}>
            Export CSV
          </Button>
        </Stack>
      </Stack>

      <Box ref={contentRef}>
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {DEALER_ACTIVITY_COLUMNS.map(column => (
                  <TableCell key={column.key} align={column.align} sx={headerCellSx}>
                    {column.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && !groups.length ? (
                <TableRow>
                  <TableCell colSpan={colSpan} sx={cellSx}>
                    <Skeleton height={160} />
                  </TableCell>
                </TableRow>
              ) : !groups.length ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={cellSx}>
                    <Typography>No data</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {groups.map(group => {
                    const flag = getCountryFlag(group.country);

                    return (
                      <React.Fragment key={group.country}>
                        <TableRow>
                          <TableCell colSpan={colSpan} sx={{ ...cellSx, bgcolor: grey[50] }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}>
                              {flag && <Box component={flag} sx={{ width: 18, height: 18 }} />}
                              <Typography variant="subtitle2" component="span">
                                {getCountryLabel(group.country)}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>

                        {group.rows.map(row => (
                          <TableRow key={row.id}>
                            {DEALER_ACTIVITY_COLUMNS.map(column => (
                              <TableCell key={column.key} align={column.align} sx={cellSx}>
                                {column.render(row)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}

                  {totalRow && (
                    <TableRow>
                      {DEALER_ACTIVITY_COLUMNS.map(column => (
                        <TableCell key={column.key} align={column.align} sx={totalCellSx}>
                          {column.render(totalRow)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );
};
